import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { toast } from "sonner";
import { Gift as GiftIcon, ArrowLeft, Loader2, Heart } from "lucide-react";
import { useCurrency, LOCAL } from "../lib/region";

export default function Gift() {
  const [params] = useSearchParams();
  const cur = useCurrency();
  const cancelled = params.get("cancelled");
  const [form, setForm] = useState({ recipient_name: "", recipient_email: "", buyer_name: "", buyer_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buy = async () => {
    if (!form.recipient_name.trim() || !form.recipient_email.trim() || !form.buyer_email.trim()) {
      toast.error("Please fill in the recipient's name and email, and your email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/gifts/checkout", { ...form, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain bg-cream" data-testid="gift-page">
      <header className="sticky top-0 z-30 bg-cream/85 backdrop-blur-md border-b border-line">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5"><img src="/logo.png" alt="Write My Story" className="h-9 w-9 object-contain" /><Wordmark size="text-lg sm:text-xl" /></Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-subtle hover:text-charcoal text-sm"><ArrowLeft size={15} /> Home</Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-12 relative z-10">
        <div className="text-center">
          <span className="inline-flex w-14 h-14 rounded-2xl bg-coral/10 text-coral items-center justify-center"><GiftIcon size={28} /></span>
          <h1 className="font-serif text-4xl text-charcoal mt-4">Give Write My Story</h1>
          <p className="text-subtle mt-3 leading-relaxed max-w-lg mx-auto">Give someone you love a full year to turn their memories into a beautiful memoir. Perfect for a parent or grandparent — no account or tech know-how needed to receive it.</p>
          <p className="mt-4 inline-flex items-center gap-2 font-serif text-2xl text-teal"><Heart size={20} className="text-coral" /> £69 <span className="text-base text-subtle font-sans">{cur !== "GBP" ? `(≈ ${LOCAL[cur].gift}) · 12 months` : "· 12 months"}</span></p>
        </div>

        {cancelled && <p data-testid="gift-cancelled" className="mt-6 text-center text-sm text-coral bg-coral/10 rounded-xl py-3">No worries — your gift wasn't purchased. You can try again whenever you're ready.</p>}

        <div className="mt-8 bg-white rounded-3xl border border-line p-6 sm:p-8 space-y-5">
          <div>
            <label className="text-sm font-medium text-charcoal">Who is this gift for?</label>
            <input data-testid="gift-recipient-name" value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} placeholder="Their name (e.g. Mum, or Margaret)" className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">Their email address</label>
            <input data-testid="gift-recipient-email" type="email" value={form.recipient_email} onChange={(e) => set("recipient_email", e.target.value)} placeholder="Where we'll send their gift code" className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div>
            <label className="text-sm font-medium text-charcoal">A short message (optional)</label>
            <textarea data-testid="gift-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Add a personal note they'll see with their gift…" className="mt-1.5 w-full min-h-[90px] p-4 rounded-xl border border-line outline-none focus:border-teal" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-line">
            <div>
              <label className="text-sm font-medium text-charcoal">Your name</label>
              <input data-testid="gift-buyer-name" value={form.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} placeholder="So they know it's from you" className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line outline-none focus:border-teal" />
            </div>
            <div>
              <label className="text-sm font-medium text-charcoal">Your email</label>
              <input data-testid="gift-buyer-email" type="email" value={form.buyer_email} onChange={(e) => set("buyer_email", e.target.value)} placeholder="For your receipt" className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line outline-none focus:border-teal" />
            </div>
          </div>
          <button data-testid="gift-buy-btn" onClick={buy} disabled={busy} className="w-full h-14 rounded-full bg-coral hover:bg-coraldark text-white font-semibold text-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" /> : <><GiftIcon size={20} /> Continue to payment</>}
          </button>
          <p className="text-xs text-subtle text-center" data-testid="gift-stripe-note">You'll receive a gift code to share, and we'll email it to your recipient too. Payments are collected by Stripe, which automatically converts currencies depending on your location.</p>
        </div>
      </main>
    </div>
  );
}
